/**
 * ReferralPrint — Vista imprimible del referido médico.
 *
 * Membrete del doctor emisor + cuerpo con paciente, especialista destino,
 * motivo, notas clínicas, urgencia y firma. Layout tamaño A4.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { ArrowLeft, Printer } from "@/components/icons";
import { toast } from "sonner";
import { doctorLabel } from "../lib/doctorLabel";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** Construye la URL wa.me con mensaje prellenado. */
function buildWhatsAppUrl(ref) {
  const phone = (ref.external_whatsapp || "").replace(/[^\d]/g, "");
  const url = `${window.location.origin}/r/${ref.public_token}`;
  const msg = encodeURIComponent(
    `Hola${
      ref.external_doctor_name ? ` ${ref.external_doctor_name}` : ""
    }, te comparto un referido médico del paciente ${ref.patient_name}. ` +
      `Motivo: ${ref.reason}. ` +
      `Puedes verlo y confirmar la recepción aquí: ${url}`
  );
  return `https://wa.me/${phone}?text=${msg}`;
}

export default function ReferralPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/referrals/${id}`)
      .then((r) => setRef(r.data))
      .catch(() => toast.error("No se pudo cargar el referido"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-black/60">
        Cargando referido…
      </div>
    );
  if (!ref) return null;

  const doctorTitle = doctorLabel(ref.from_doctor_name);
  const specialty = ref.from_doctor_specialty || "";
  const exequatur = ref.exequatur_number || "";

  const targetLine = ref.to_doctor_id
    ? `${doctorLabel(ref.to_doctor_name || "—")}${
        ref.to_doctor_specialty ? ` — ${ref.to_doctor_specialty}` : ""
      }`
    : `${ref.external_specialty || "Especialista"}${
        ref.external_doctor_name ? ` — ${ref.external_doctor_name}` : ""
      }${ref.external_center ? ` (${ref.external_center})` : ""}`;

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-8 print:bg-white print:py-0">
      {/* Toolbar — oculta al imprimir */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between mb-4 print:hidden flex-wrap gap-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-black/70 hover:text-black"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {ref.public_token && ref.external_whatsapp && (
            <button
              type="button"
              onClick={() => window.open(buildWhatsAppUrl(ref), "_blank", "noopener,noreferrer")}
              data-testid="ref-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#25D366] hover:bg-[#20b358] text-white font-bold text-sm shadow"
            >
              Enviar por WhatsApp
            </button>
          )}
          {ref.public_token && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/r/${ref.public_token}`
                );
                toast.success("Enlace copiado al portapapeles");
              }}
              data-testid="ref-copy-link-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-[#01BC8E] text-sm font-bold text-[#163144]"
            >
              Copiar enlace
            </button>
          )}
          <button
            onClick={() => window.print()}
            data-testid="ref-print-btn"
            className="btn-primary"
          >
            <Printer className="w-4 h-4 inline mr-1.5" />
            Imprimir / Guardar PDF
          </button>
        </div>
      </div>

      {/* Documento */}
      <div
        className="print-page max-w-4xl mx-auto bg-white shadow-[0_10px_40px_rgba(0,0,0,0.08)] px-10 py-8 print:shadow-none print:max-w-none print:px-14 print:py-10"
        data-testid="referral-document"
      >
        {/* Membrete */}
        <div className="text-center">
          {ref.clinic_logo_url ? (
            <div className="flex items-center justify-center gap-3 mb-2">
              <img
                src={ref.clinic_logo_url}
                alt="Logo clínica"
                className="h-14 object-contain"
              />
              {ref.clinic_name && (
                <div className="text-left">
                  <div className="text-sm font-extrabold uppercase tracking-tight leading-tight text-[#1E4FBF]">
                    {ref.clinic_name}
                  </div>
                </div>
              )}
            </div>
          ) : (
            ref.clinic_name && (
              <div className="text-lg font-extrabold uppercase tracking-tight mb-2 text-black">
                {ref.clinic_name}
              </div>
            )
          )}
          <div className="text-base font-extrabold text-black">
            {doctorTitle}
            {specialty ? ` — ${specialty}` : ""}
          </div>
          {ref.clinic_address && (
            <div className="text-sm text-black mt-1 leading-snug max-w-2xl mx-auto">
              {ref.clinic_address}
            </div>
          )}
          {ref.clinic_phone && (
            <div className="text-sm font-bold text-black mt-0.5">
              {ref.clinic_phone}
            </div>
          )}
        </div>

        <div className="border-t border-black/10 my-6" />

        {/* Título del documento */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-black/50">
              Referido médico
            </div>
            {ref.urgency === "urgente" && (
              <div className="inline-block mt-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-red-100 text-red-700">
                Urgente
              </div>
            )}
          </div>
          <div className="text-right text-sm text-black">
            <div className="font-bold">{fmtDate(ref.created_at)}</div>
            {exequatur && (
              <div className="text-xs text-black/60 mt-0.5">
                Exequátur: {exequatur}
              </div>
            )}
          </div>
        </div>

        {/* Destinatario */}
        <div className="bg-[#F5F5F5] rounded-md p-5 mb-4">
          <div className="text-xs uppercase tracking-wider font-bold text-black/50">
            Dirigido a
          </div>
          <div className="text-base font-extrabold text-black mt-1">
            {targetLine}
          </div>
        </div>

        {/* Paciente */}
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-black/50">
              Paciente
            </div>
            <div className="text-base font-bold text-black mt-1">
              {ref.patient_name || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-black/50">
              Emitido por
            </div>
            <div className="text-base font-bold text-black mt-1">
              {doctorTitle}
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="bg-[#F5F5F5] rounded-md p-5 text-sm leading-relaxed text-black">
          <p className="mb-3">
            Estimado(a) colega, me permito referirle al paciente{" "}
            <span className="font-bold">{ref.patient_name}</span> para su
            evaluación y manejo especializado.
          </p>
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wider font-bold text-black/50">
              Motivo del referido
            </div>
            <div className="mt-1">{ref.reason}</div>
          </div>
          {ref.clinical_notes && (
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-black/50">
                Notas clínicas
              </div>
              <div className="mt-1 whitespace-pre-line">{ref.clinical_notes}</div>
            </div>
          )}
          <p className="mt-4">
            Agradezco de antemano su atención y quedo a la orden para cualquier
            información adicional.
          </p>
        </div>

        {/* Firma, sello */}
        <div className="mt-24 relative">
          <div className="flex flex-col items-center">
            <div className="relative">
              {ref.stamp_url && (
                <img
                  src={ref.stamp_url}
                  alt="Sello"
                  className="absolute -top-14 left-1/2 -translate-x-1/2 h-32 opacity-80 pointer-events-none"
                />
              )}
              {ref.signature_url && (
                <img
                  src={ref.signature_url}
                  alt="Firma"
                  className="relative h-16 object-contain z-10"
                />
              )}
            </div>
            <div className="w-80 border-t border-black mt-1 pt-2 text-center">
              <div className="text-sm font-bold text-black">{doctorTitle}</div>
              {specialty && (
                <div className="text-sm text-black">{specialty}</div>
              )}
            </div>
          </div>
        </div>

        {/* Bloque enlace público / QR — solo para referidos externos */}
        {ref.public_token && (
          <div className="mt-10 border-t border-dashed border-black/20 pt-6">
            <div className="flex items-start gap-5 flex-wrap sm:flex-nowrap">
              <div className="shrink-0 border-2 border-black/10 rounded-lg p-2 bg-white">
                <QRCodeSVG
                  value={`${window.location.origin}/r/${ref.public_token}`}
                  size={110}
                  level="M"
                  data-testid="referral-qr"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-black/50">
                  Acceso digital para el especialista
                </div>
                <div className="text-sm text-black mt-1">
                  Escanea el código QR o abre el siguiente enlace desde cualquier
                  dispositivo para consultar este referido en línea y confirmar su recepción.
                </div>
                <div className="mt-2 inline-block bg-slate-50 border border-black/10 rounded-md px-3 py-1.5 text-xs font-mono break-all">
                  {`${window.location.origin}/r/${ref.public_token}`}
                </div>
                <div className="text-[10px] text-black/40 mt-2 uppercase tracking-wider">
                  Enlace válido 90 días
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer plataforma */}
        <div className="mt-10 pt-4 border-t border-black/10 flex items-center justify-center gap-3">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-6 w-6 object-contain opacity-70"
          />
          <div className="text-xs text-black/50 leading-tight">
            <div className="font-bold text-black/70">ConsuDoctor</div>
            <div>www.consudoctor.com</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          /* Ancho A4 exacto (210mm), altura automática según contenido */
          @page { size: 210mm auto; margin: 12mm; }
          html, body { background: #fff !important; }
          body * { visibility: hidden; }
          .print-page, .print-page * { visibility: visible; }
          .print-page {
            position: absolute;
            left: 0; top: 0;
            width: 186mm !important;   /* 210mm - 2 × 12mm de margen */
            max-width: 186mm !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
